import { createTheme } from '@mui/material/styles';

// Waltero Brand Color Palette - Official Brand Guidelines Compliant
const walteroColors = {
  primary: {
    // Blues - Primary Brand Colors (60% of interface)
    deepBlue: '#023861',      // Darkest blue for primary navigation/headers
    coreBlue: '#094872',      // Core brand blue for main elements  
    mediumBlue: '#23678b',    // PRIMARY brand blue for logos and key brand marks
    lightBlue: '#3d8dae',     // PRIMARY brand blue for interactive elements and hover states
    paleBlue: '#98c3cf',      // Pale blue for subtle backgrounds
    neutralBlue: '#d7e2e4',   // Very light blue for cards/containers
    backgroundBlue: '#edefef' // Lightest blue for main backgrounds
  },
  secondary: {
    // Apricots/Peach - Secondary Brand Colors (30% of interface)
    darkApricot: '#c35d2c',   // Dark apricot for hover states and emphasis
    primaryApricot: '#f18849', // Primary apricot for CTAs and highlights
    lightApricot: '#f4994c',  // Light apricot for accents and tags
    paleApricot: '#ffead3',   // Pale apricot for section backgrounds
    backgroundApricot: '#fdf3eb' // Lightest apricot for alternating backgrounds
  },
  tertiary: {
    // Sage Green - Secondary Supportive Colors (10% of interface)
    sageGreenDark: '#6F8F7B',    // Secondary buttons, supportive UI elements
    sageGreenMedium: '#A4BDA8',  // Success indicators, positive states
    sageGreenLight: '#C9D6CA',   // Form backgrounds, input fields, subtle backgrounds
  },
  // Semantic Colors - Brand Compliant
  success: '#A4BDA8',   // Brand-compliant Sage Green for success states
  warning: '#f18849',   // Brand apricot for warnings
  error: '#c35d2c',     // Brand dark apricot for errors
  info: '#3d8dae',      // Brand light blue for info
  
  // Fleet Status Colors - Brand Compliant System
  fleetStatus: {
    success: '#A4BDA8',   // Brand Sage Green for success/good performance
    warning: '#f18849',   // Brand Apricot for warnings/medium performance  
    critical: '#c35d2c',  // Brand Dark Apricot for critical/bad performance
    noData: '#616161'     // Neutral grey for no data states
  },
  
  // Neutral Colors
  white: '#ffffff',
  black: '#000000',
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121'
  }
};

// Typography Configuration
const walteroTypography = {
  fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
  fontFamilyFallback: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
  
  // Type Scale following Waltero brand guidelines
  h1: {
    fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
    fontWeight: 800, // ExtraBold
    fontSize: '4rem', // 64px
    lineHeight: 1.1,
    letterSpacing: '-0.01em', // -1%
    textTransform: 'none',
    color: walteroColors.primary.deepBlue
  },
  h2: {
    fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
    fontWeight: 700, // Bold
    fontSize: '3rem', // 48px
    lineHeight: 1.2,
    letterSpacing: '-0.005em', // -0.5%
    textTransform: 'none',
    color: walteroColors.primary.deepBlue
  },
  h3: {
    fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
    fontWeight: 600, // SemiBold
    fontSize: '2rem', // 32px
    lineHeight: 1.3,
    letterSpacing: '0em',
    textTransform: 'none',
    color: walteroColors.primary.coreBlue
  },
  h4: {
    fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
    fontWeight: 600, // SemiBold
    fontSize: '1.5rem', // 24px
    lineHeight: 1.3,
    letterSpacing: '0em',
    textTransform: 'none',
    color: walteroColors.primary.coreBlue
  },
  h5: {
    fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
    fontWeight: 500, // Medium
    fontSize: '1.25rem', // 20px
    lineHeight: 1.4,
    letterSpacing: '0em',
    textTransform: 'none',
    color: walteroColors.primary.coreBlue
  },
  h6: {
    fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
    fontWeight: 500, // Medium
    fontSize: '1.125rem', // 18px
    lineHeight: 1.4,
    letterSpacing: '0em',
    textTransform: 'none',
    color: walteroColors.primary.coreBlue
  },
  body1: {
    fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
    fontWeight: 400, // Regular
    fontSize: '1.25rem', // 20px - Body L
    lineHeight: 1.5,
    letterSpacing: '0em',
    color: walteroColors.primary.coreBlue
  },
  body2: {
    fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
    fontWeight: 400, // Regular
    fontSize: '1rem', // 16px - Body M
    lineHeight: 1.6,
    letterSpacing: '0em',
    color: walteroColors.primary.coreBlue
  },
  caption: {
    fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
    fontWeight: 400, // Regular
    fontSize: '0.875rem', // 14px - Body S
    lineHeight: 1.4,
    letterSpacing: '0em',
    color: walteroColors.primary.mediumBlue
  },
  button: {
    fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
    fontWeight: 500, // Medium
    fontSize: '1rem', // 16px
    lineHeight: 1.2,
    letterSpacing: '0.01em', // +1%
    textTransform: 'uppercase',
    color: walteroColors.white
  }
};

// Component Customizations
const componentOverrides = {
  MuiCssBaseline: {
    styleOverrides: {
      '@font-face': [
        {
          fontFamily: 'Mozaic Geo',
          fontStyle: 'normal',
          fontDisplay: 'swap',
          fontWeight: 400,
          // FONT LOADING INSTRUCTIONS:
          // 1. Place Mozaic Geo font files in public/fonts/
          // 2. Add src: url() declarations below
          // 3. Or integrate with font service/CDN
          // Currently using Inter as fallback (loaded via Google Fonts)
        }
      ],
      body: {
        backgroundColor: walteroColors.primary.backgroundBlue,
        fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif'
      }
    }
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: walteroColors.primary.deepBlue,
        color: walteroColors.white,
        borderRadius: 0,
        boxShadow: 'none',
        borderBottom: `1px solid ${walteroColors.primary.coreBlue}`
      }
    }
  },
  MuiCard: {
    styleOverrides: {
      root: {
        backgroundColor: walteroColors.white,
        borderRadius: '12px',
        boxShadow: '0px 2px 12px rgba(2, 56, 97, 0.08)',
        border: `1px solid ${walteroColors.primary.neutralBlue}`,
        '&:hover': {
          boxShadow: '0px 4px 20px rgba(2, 56, 97, 0.12)',
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out'
        }
      }
    }
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        textTransform: 'uppercase',
        fontWeight: 500,
        letterSpacing: '0.01em',
        padding: '12px 24px',
        transition: 'all 0.2s ease-in-out'
      },
      contained: {
        backgroundColor: walteroColors.secondary.primaryApricot,
        color: walteroColors.white,
        boxShadow: '0px 2px 8px rgba(241, 136, 73, 0.25)',
        '&:hover': {
          backgroundColor: walteroColors.secondary.darkApricot,
          boxShadow: '0px 4px 16px rgba(195, 93, 44, 0.35)',
          transform: 'translateY(-1px)'
        },
        '&:active': {
          transform: 'translateY(0px)'
        }
      },
      outlined: {
        borderColor: walteroColors.primary.lightBlue,
        color: walteroColors.primary.lightBlue,
        '&:hover': {
          backgroundColor: walteroColors.primary.lightBlue,
          color: walteroColors.white,
          borderColor: walteroColors.primary.lightBlue
        }
      },
      text: {
        color: walteroColors.primary.mediumBlue,
        '&:hover': {
          backgroundColor: `${walteroColors.primary.paleBlue}20`,
          color: walteroColors.primary.coreBlue
        }
      }
    }
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: '6px',
        fontWeight: 500,
        fontSize: '0.875rem'
      },
      filled: {
        backgroundColor: walteroColors.primary.lightBlue,
        color: walteroColors.white,
        '&.MuiChip-colorSuccess': {
          backgroundColor: walteroColors.success,
          color: walteroColors.white
        },
        '&.MuiChip-colorWarning': {
          backgroundColor: walteroColors.secondary.lightApricot,
          color: walteroColors.white
        },
        '&.MuiChip-colorError': {
          backgroundColor: walteroColors.secondary.darkApricot,
          color: walteroColors.white
        }
      }
    }
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          '& fieldset': {
            borderColor: walteroColors.primary.neutralBlue
          },
          '&:hover fieldset': {
            borderColor: walteroColors.primary.lightBlue
          },
          '&.Mui-focused fieldset': {
            borderColor: walteroColors.secondary.primaryApricot,
            borderWidth: '2px'
          }
        },
        '& .MuiInputLabel-root.Mui-focused': {
          color: walteroColors.secondary.primaryApricot
        }
      }
    }
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundColor: walteroColors.white,
        borderRadius: '12px'
      },
      elevation1: {
        boxShadow: '0px 2px 8px rgba(2, 56, 97, 0.08)'
      },
      elevation2: {
        boxShadow: '0px 4px 12px rgba(2, 56, 97, 0.12)'
      },
      elevation3: {
        boxShadow: '0px 6px 16px rgba(2, 56, 97, 0.16)'
      }
    }
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        backgroundColor: walteroColors.secondary.backgroundApricot,
        '& .MuiTableCell-head': {
          color: walteroColors.primary.coreBlue,
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.01em'
        }
      }
    }
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:nth-of-type(even)': {
          backgroundColor: `${walteroColors.primary.backgroundBlue}50`
        },
        '&:hover': {
          backgroundColor: `${walteroColors.secondary.backgroundApricot}80`
        }
      }
    }
  }
};

// Animation and transition configuration
const walteroAnimations = {
  // Standard easing curves
  easing: {
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    // Custom Waltero easing for smooth mobile interactions
    walteroEase: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    walteroEaseOut: 'cubic-bezier(0.215, 0.61, 0.355, 1)'
  },
  
  // Duration values optimized for mobile
  duration: {
    shortest: 150,
    shorter: 200,
    short: 250,
    standard: 300,
    complex: 375,
    enteringScreen: 225,
    leavingScreen: 195,
    // Custom durations
    micro: 100,
    gentle: 400,
    slow: 500
  }
};

// Create the Waltero theme
const walteroTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: walteroColors.primary.mediumBlue,  // #23678b - PRIMARY brand color per guidelines
      dark: walteroColors.primary.coreBlue,    // #094872 - Darker primary
      light: walteroColors.primary.lightBlue,  // #3d8dae - PRIMARY brand color per guidelines
      contrastText: walteroColors.white
    },
    secondary: {
      main: walteroColors.secondary.primaryApricot,
      dark: walteroColors.secondary.darkApricot,
      light: walteroColors.secondary.lightApricot,
      contrastText: walteroColors.white
    },
    success: {
      main: walteroColors.success,
      contrastText: walteroColors.white
    },
    warning: {
      main: walteroColors.warning,
      contrastText: walteroColors.white
    },
    error: {
      main: walteroColors.error,
      contrastText: walteroColors.white
    },
    info: {
      main: walteroColors.info,
      contrastText: walteroColors.white
    },
    background: {
      default: walteroColors.primary.backgroundBlue,
      paper: walteroColors.white
    },
    text: {
      primary: walteroColors.primary.coreBlue,
      secondary: walteroColors.primary.mediumBlue
    },
    grey: walteroColors.grey,
    // Custom Waltero color extensions
    waltero: {
      ...walteroColors,
      // Add status shortcuts for component compatibility
      status: {
        success: walteroColors.success,
        warning: walteroColors.warning,  
        error: walteroColors.error,
        info: walteroColors.info
      },
      neutral: {
        lightGray: walteroColors.grey[200],
        mediumGray: walteroColors.grey[500],
        darkGray: walteroColors.grey[800]
      },
      // Brand color balance shortcuts (60/30/10 guideline)
      brandBalance: {
        primary60: walteroColors.primary,     // 60% Blue spectrum
        secondary30: walteroColors.secondary, // 30% Apricot spectrum  
        tertiary10: walteroColors.tertiary    // 10% Sage Green spectrum
      }
    }
  },
  typography: walteroTypography,
  spacing: 8, // 8px base spacing unit
  shape: {
    borderRadius: 8
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920
    }
  },
  // Enhanced transitions with mobile optimizations
  transitions: {
    easing: walteroAnimations.easing,
    duration: walteroAnimations.duration,
    create: (props = ['all'], options = {}) => {
      const {
        duration = walteroAnimations.duration.standard,
        easing = walteroAnimations.easing.walteroEase,
        delay = 0,
      } = options;

      return (Array.isArray(props) ? props : [props])
        .map(animatedProp => `${animatedProp} ${duration}ms ${easing} ${delay}ms`)
        .join(',');
    }
  },
  // Custom Waltero animations
  waltero: {
    animations: walteroAnimations,
    // Predefined animation classes for common use cases
    animationClasses: {
      fadeIn: {
        animation: 'walteroFadeIn 300ms ease-out',
        '@keyframes walteroFadeIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' }
        }
      },
      slideInUp: {
        animation: 'walteroSlideInUp 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        '@keyframes walteroSlideInUp': {
          from: { opacity: 0, transform: 'translateY(40px)' },
          to: { opacity: 1, transform: 'translateY(0)' }
        }
      },
      scaleIn: {
        animation: 'walteroScaleIn 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        '@keyframes walteroScaleIn': {
          from: { opacity: 0, transform: 'scale(0.9)' },
          to: { opacity: 1, transform: 'scale(1)' }
        }
      },
      float: {
        animation: 'walteroFloat 2s ease-in-out infinite',
        '@keyframes walteroFloat': {
          '0%, 100%': { transform: 'translateY(0px)' },
          '50%': { transform: 'translateY(-5px)' }
        }
      }
    }
  },
  components: componentOverrides
});

export default walteroTheme;
export { walteroColors, walteroTypography };